import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, IconButton, Checkbox, Chip, Tooltip } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { handleDownloadDocument, notifySomething, documentTypeOptions, vehicleToString } from '../_Utils/AppDocuCommon';
import AppDocuServiceCall from '../_Utils/AppDocuServiceCall';
import DialogDocumentDetails from './DialogDocumentDetails';

export default class AssocDocumentDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      availableDocuments: [],
      associatedDocuments: [],
      documentNameFilter: '',
      detailsDialogId: null,
    };
  }

  async componentDidMount() {
    await this.fetchDocuments();
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.vehicleData &&
      (prevProps.vehicleData?.id !== this.props.vehicleData?.id || !prevProps.vehicleData && this.props.vehicleData)
    ) {
      await this.fetchDocuments();
    }
  }

  fetchDocuments = async () => {
    if (!this.props.vehicleData) return;
    this.setState({ loading: true });
    try {
      const allDocs = await AppDocuServiceCall.listDocuments();
      const assocDocsResp = await AppDocuServiceCall.getVehicleDocuments(this.props.vehicleData.id);
      // Crea una mappa id -> info associazione (username, link_created_at)
      const assocDocsMap = {};
      (assocDocsResp.data.documents || []).forEach(d => {
        assocDocsMap[d.id] = {
          username: d.username,
          link_created_at: d.link_created_at
        };
      });
      // Unisci info di associazione ai documenti
      const mergedDocs = (allDocs.data.documents || []).map(doc => ({
        ...doc,
        ...(assocDocsMap[doc.id] || {})
      }));
      this.setState({
        availableDocuments: mergedDocs,
        associatedDocuments: Object.keys(assocDocsMap).map(id => Number(id)),
        loading: false
      });
    } catch (error) {
      notifySomething('Errore nel caricamento documenti', 'error');
      this.setState({ loading: false });
    }
  };

  handleAssocDocumentToggle = async (id, checked) => {
    if (!this.props.vehicleData) return;
    const msg = checked ? 'Vuoi davvero associare questo documento alla macchina?' : 'Vuoi davvero disassociare questo documento dalla macchina?';
    if (!window.confirm(msg)) return;
    this.setState({ loading: true });
    try {
      let res;
      if (checked) {
        res = await AppDocuServiceCall.addVehicleDocument(this.props.vehicleData.id, id);
      } else {
        res = await AppDocuServiceCall.removeVehicleDocument(this.props.vehicleData.id, id);
      }
      if (res.data && res.data.is_valid === false) {
        notifySomething(res.data.message || 'Errore aggiornamento associazione', 'error');
        this.setState({ loading: false });
        return;
      }
      await this.fetchDocuments();
    } catch (error) {
      notifySomething('Errore aggiornamento associazione', 'error');
      this.setState({ loading: false });
    }
  };

  render() {
    const { availableDocuments, associatedDocuments, documentNameFilter, loading } = this.state;
    // Filtra e ordina
    const associated = availableDocuments.filter(d => associatedDocuments.includes(d.id)).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const notAssociated = availableDocuments.filter(d => !associatedDocuments.includes(d.id)).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    let all = [...associated, ...notAssociated];
    if (documentNameFilter.trim() !== '') {
      const filter = documentNameFilter.trim().toLowerCase();
      all = all.filter(item => {
        const nameMatch = (item.name || '').toLowerCase().includes(filter);
        const origFileMatch = (item.original_filename || '').toLowerCase().includes(filter);
        return nameMatch || origFileMatch;
      });
    }
    return (
      <Dialog open={!!this.props.vehicleData} onClose={this.props.onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          Associate Documents
          {this.props.vehicleData && vehicleToString(this.props.vehicleData.id, this.props.vehicleData)}          
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto', overflow: 'visible' }}>
          <Stack spacing={1}>
            <TextField
              fullWidth
              size="small"
              label="Filtra per nome documento"
              value={documentNameFilter}
              onChange={e => this.setState({ documentNameFilter: e.target.value })}
              disabled={loading}
            />
            {all.map(item => {
              // Mostra solo se associato (username e link_created_at sono presenti solo per associati)
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', width: '100%', minHeight: 36 }}>
                  <Checkbox
                    checked={associatedDocuments.includes(item.id)}
                    onChange={e => this.handleAssocDocumentToggle(item.id, e.target.checked)}
                    disabled={loading}
                    sx={{ p: 0.5, mr: 0 }}
                  />
                  <Tooltip title="Download document">
                    <IconButton size="small" onClick={() => handleDownloadDocument(item)} sx={{ mr: 0, verticalAlign: 'middle' }}>
                      <InsertDriveFileIcon fontSize="small" sx={{ display: 'none' }} />
                      <span className="fa fa-download" style={{ fontSize: 20, color: 'black', verticalAlign: 'middle', display: 'inline-block', marginTop: 3 }}></span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={(documentTypeOptions.find(opt => opt.name === item.document_type)?.label) || item.document_type}>
                    <InsertDriveFileIcon fontSize="small" sx={{ fontSize: 20, color: (documentTypeOptions.find(opt => opt.name === item.document_type)?.color) || 'info.main', mr: 1 }} />
                  </Tooltip>
                  <span
                    style={{ fontWeight: associatedDocuments.includes(item.id) ? 600 : 400, fontSize: 15, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}
                    onClick={() => this.setState({ detailsDialogId: item.id })}
                  >
                    {item.name} <span style={{ color: '#888', fontSize: 13 }}>[{item.original_filename}]</span>
                    {item.language && (
                      <Chip label={item.language} size="small" sx={{ bgcolor: 'primary.main', color: 'white', height: 20, fontSize: 12, ml: 1, verticalAlign: 'middle' }} />
                    )}
                  </span>
                  {(item.username || item.link_created_at) && (
                    <span style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#555', whiteSpace: 'nowrap' }}>
                      {item.username && <span>👤 {item.username}</span>}
                      {item.link_created_at && <span>🕒 {new Date(item.link_created_at).toLocaleString()}</span>}
                    </span>
                  )}
                </div>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose}>Close</Button>
        </DialogActions>
        <DialogDocumentDetails
          open={!!this.state.detailsDialogId}
          documentId={this.state.detailsDialogId}
          onClose={() => this.setState({ detailsDialogId: null })}
        />
      </Dialog>
    );
  }
}
